import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  FolderTree,
  ChevronRight,
  ChevronDown,
  Loader2,
  ExternalLink,
  RefreshCw,
  Download,
  Settings,
  Image as ImageIcon,
  Check,
  X,
  Cloud,
  CloudDownload,
  Clock,
} from 'lucide-react';
import api from '@/lib/api';

const AccessoiresFournisseurPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Selection for bulk actions
  const [selectedArticles, setSelectedArticles] = useState([]);
  
  // Dialog states
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form states
  const [articleForm, setArticleForm] = useState({
    name: '',
    reference: '',
    category_id: '',
    description: '',
    short_description: '',
    price: '',
    selling_price: '',
    margin_percent: '',
    price_on_request: false,
    image_url: '',
    compatible_tonnage: '',
    is_active: true,
  });
  
  // Margin dialog
  const [marginDialogOpen, setMarginDialogOpen] = useState(false);
  const [marginValue, setMarginValue] = useState('25');
  const [applyMarginTo, setApplyMarginTo] = useState('selected'); // 'selected', 'category', 'all'
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    parent_id: '',
    description: '',
    order: 0,
  });
  
  const [saving, setSaving] = useState(false);
  
  // Scraping state
  const [scrapeDialogOpen, setScrapeDialogOpen] = useState(false);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  const [scrapeInterval, setScrapeInterval] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchStats();
    checkScrapeStatus();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, searchQuery, showInactive, page]);
  
  // Poll scrape status when scraping is in progress
  useEffect(() => {
    if (scrapingInProgress) {
      const interval = setInterval(checkScrapeStatus, 5000);
      setScrapeInterval(interval);
      return () => clearInterval(interval);
    } else if (scrapeInterval) {
      clearInterval(scrapeInterval);
      setScrapeInterval(null);
    }
  }, [scrapingInProgress]);
  
  const checkScrapeStatus = async () => {
    try {
      const response = await api.get('/accessoires-fournisseur/scrape/status');
      setScrapeStatus(response.data);
      setScrapingInProgress(response.data.is_running);
      
      // Refresh data if scraping just completed
      if (!response.data.is_running && response.data.last_result) {
        fetchArticles();
        fetchStats();
      }
    } catch (error) {
      console.error('Error checking scrape status:', error);
    }
  };
  
  const handleStartScrape = async (categorySlugs = null) => {
    try {
      const response = await api.post('/accessoires-fournisseur/scrape/start', {
        category_slugs: categorySlugs
      });
      toast.success(response.data.message);
      setScrapingInProgress(true);
      setScrapeDialogOpen(false);
      checkScrapeStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du démarrage du scraping');
    }
  };
  
  const handleSyncCategories = async () => {
    try {
      const response = await api.post('/accessoires-fournisseur/scrape/sync-categories');
      toast.success(response.data.message);
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la synchronisation des catégories');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/accessoires-fournisseur/categories?include_inactive=true');
      setCategories(response.data.categories);
      setAllCategories(response.data.all);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category_id', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      if (!showInactive) params.append('is_active', 'true');
      params.append('skip', page * limit);
      params.append('limit', limit);
      
      const response = await api.get(`/accessoires-fournisseur/articles?${params.toString()}`);
      setArticles(response.data.articles);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/accessoires-fournisseur/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleInitCategories = async () => {
    try {
      const response = await api.post('/accessoires-fournisseur/init-categories');
      toast.success(response.data.message);
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleToggleArticle = async (articleId) => {
    try {
      const response = await api.put(`/accessoires-fournisseur/articles/${articleId}/toggle-active`);
      toast.success(response.data.message);
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleToggleShowPrice = async (articleId) => {
    try {
      const article = articles.find(a => a.id === articleId);
      const newShowPrice = article?.show_price === false ? true : false;
      
      await api.put(`/accessoires-fournisseur/articles/${articleId}`, {
        show_price: newShowPrice
      });
      
      toast.success(`Prix ${newShowPrice ? 'visible' : 'masqué'} sur le site public`);
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleToggleCategoryPrices = async (show) => {
    if (!selectedCategory) {
      toast.error('Sélectionnez une catégorie');
      return;
    }
    
    try {
      const response = await api.patch(`/accessoires-fournisseur/categories/${selectedCategory}/toggle-prices?show_prices=${show}`);
      toast.success(response.data.message);
      fetchArticles();
      fetchCategories();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleBulkToggle = async (isActive) => {
    if (selectedArticles.length === 0) {
      toast.error('Sélectionnez au moins un article');
      return;
    }
    
    try {
      const response = await api.post('/accessoires-fournisseur/bulk-toggle', {
        article_ids: selectedArticles,
        is_active: isActive,
      });
      toast.success(response.data.message);
      setSelectedArticles([]);
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleApplyMargin = async () => {
    const margin = parseFloat(marginValue);
    if (isNaN(margin) || margin < 0) {
      toast.error('Marge invalide');
      return;
    }
    
    try {
      const payload = { margin_percent: margin };
      
      if (applyMarginTo === 'selected' && selectedArticles.length > 0) {
        payload.article_ids = selectedArticles;
      } else if (applyMarginTo === 'category' && selectedCategory) {
        payload.category_id = selectedCategory;
      }
      // Si 'all', on n'envoie rien de plus
      
      const response = await api.post('/accessoires-fournisseur/bulk-margin', payload);
      toast.success(response.data.message);
      setMarginDialogOpen(false);
      setSelectedArticles([]);
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de l\'application de la marge');
    }
  };

  // Calculer le prix de vente quand on change le prix ou la marge
  const handlePriceChange = (value) => {
    setArticleForm(prev => {
      const price = parseFloat(value) || 0;
      const margin = parseFloat(prev.margin_percent) || 0;
      const selling = margin > 0 ? Math.round(price * (1 + margin / 100) * 100) / 100 : prev.selling_price;
      return { ...prev, price: value, selling_price: selling ? selling.toString() : '' };
    });
  };

  const handleMarginChange = (value) => {
    setArticleForm(prev => {
      const price = parseFloat(prev.price) || 0;
      const margin = parseFloat(value) || 0;
      const selling = price > 0 && margin > 0 ? Math.round(price * (1 + margin / 100) * 100) / 100 : '';
      return { ...prev, margin_percent: value, selling_price: selling ? selling.toString() : '' };
    });
  };

  const handleSellingPriceChange = (value) => {
    setArticleForm(prev => {
      const price = parseFloat(prev.price) || 0;
      const selling = parseFloat(value) || 0;
      const margin = price > 0 && selling > 0 ? Math.round((selling / price - 1) * 100 * 100) / 100 : '';
      return { ...prev, selling_price: value, margin_percent: margin ? margin.toString() : '' };
    });
  };

  const handleSaveArticle = async () => {
    if (!articleForm.name || !articleForm.category_id) {
      toast.error('Nom et catégorie requis');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...articleForm,
        price: articleForm.price ? parseFloat(articleForm.price) : null,
        selling_price: articleForm.selling_price ? parseFloat(articleForm.selling_price) : null,
        margin_percent: articleForm.margin_percent ? parseFloat(articleForm.margin_percent) : null,
      };
      
      if (editingArticle) {
        await api.put(`/accessoires-fournisseur/articles/${editingArticle.id}`, data);
        toast.success('Article mis à jour');
      } else {
        await api.post('/accessoires-fournisseur/articles', data);
        toast.success('Article créé');
      }
      
      setArticleDialogOpen(false);
      setEditingArticle(null);
      resetArticleForm();
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name || !categoryForm.slug) {
      toast.error('Nom et slug requis');
      return;
    }
    
    setSaving(true);
    try {
      if (editingCategory) {
        await api.put(`/accessoires-fournisseur/categories/${editingCategory.id}`, categoryForm);
        toast.success('Catégorie mise à jour');
      } else {
        await api.post('/accessoires-fournisseur/categories', categoryForm);
        toast.success('Catégorie créée');
      }
      
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      resetCategoryForm();
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      await api.delete(`/accessoires-fournisseur/articles/${articleId}`);
      toast.success('Article supprimé');
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Supprimer cette catégorie ? Les articles seront désactivés.')) return;
    
    try {
      await api.delete(`/accessoires-fournisseur/categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetArticleForm = () => {
    setArticleForm({
      name: '',
      reference: '',
      category_id: '',
      description: '',
      short_description: '',
      price: '',
      selling_price: '',
      margin_percent: '',
      price_on_request: false,
      image_url: '',
      compatible_tonnage: '',
      is_active: true,
    });
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      slug: '',
      parent_id: '',
      description: '',
      order: 0,
    });
  };

  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      name: article.name || '',
      reference: article.reference || '',
      category_id: article.category_id || '',
      description: article.description || '',
      short_description: article.short_description || '',
      price: article.price?.toString() || '',
      selling_price: article.selling_price?.toString() || '',
      margin_percent: article.margin_percent?.toString() || '',
      price_on_request: article.price_on_request || false,
      image_url: article.image_url || '',
      compatible_tonnage: article.compatible_tonnage || '',
      is_active: article.is_active !== false,
    });
    setArticleDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      slug: category.slug || '',
      parent_id: category.parent_id || '',
      description: category.description || '',
      order: category.order || 0,
    });
    setCategoryDialogOpen(true);
  };

  const toggleCategoryExpand = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedArticles(articles.map(a => a.id));
    } else {
      setSelectedArticles([]);
    }
  };

  const handleSelectArticle = (articleId, checked) => {
    if (checked) {
      setSelectedArticles(prev => [...prev, articleId]);
    } else {
      setSelectedArticles(prev => prev.filter(id => id !== articleId));
    }
  };

  const formatPrice = (price, priceOnRequest) => {
    if (priceOnRequest) return 'Sur demande';
    if (!price) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const renderCategoryTree = (cats, level = 0) => {
    return cats.map(cat => {
      const hasChildren = cat.children && cat.children.length > 0;
      const isExpanded = expandedCategories[cat.id];
      const isSelected = selectedCategory === cat.id;
      
      return (
        <div key={cat.id}>
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
              isSelected ? 'bg-orange-100 text-orange-800' : 'hover:bg-slate-100'
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
            onClick={() => setSelectedCategory(isSelected ? null : cat.id)}
          >
            {hasChildren && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleCategoryExpand(cat.id); }}
                className="p-0.5 hover:bg-slate-200 rounded"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            {!hasChildren && <span className="w-5" />}
            <span className="flex-1 text-sm font-medium truncate">{cat.name}</span>
            {!cat.is_active && <Badge variant="outline" className="text-xs">Inactif</Badge>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <MoreVertical className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }}>
                  <Edit className="w-4 h-4 mr-2" /> Modifier
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {hasChildren && isExpanded && renderCategoryTree(cat.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <div className="space-y-6" data-testid="accessoires-fournisseur-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Accessoires & Pièces Fournisseur
          </h1>
          <p className="text-slate-500 mt-1">
            Catalogue Toomat - Gérez les accessoires disponibles
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scraping status indicator */}
          {scrapingInProgress && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 animate-pulse">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Scraping en cours...
            </Badge>
          )}
          
          {/* Scrape button */}
          <Button 
            onClick={() => setScrapeDialogOpen(true)} 
            variant="outline" 
            className="border-blue-300 text-blue-700"
            disabled={scrapingInProgress}
          >
            <CloudDownload className="w-4 h-4 mr-2" />
            Importer TOOMAT
          </Button>
          
          {allCategories.length === 0 && (
            <Button onClick={handleInitCategories} variant="outline" className="border-green-300 text-green-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Initialiser catégories
            </Button>
          )}
          <Button onClick={() => { resetCategoryForm(); setCategoryDialogOpen(true); }} variant="outline">
            <FolderTree className="w-4 h-4 mr-2" />
            Nouvelle catégorie
          </Button>
          <Button onClick={() => { resetArticleForm(); setArticleDialogOpen(true); }} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel article
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-800">{stats.total_articles}</div>
              <div className="text-sm text-slate-500">Articles total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{stats.active_articles}</div>
              <div className="text-sm text-slate-500">Actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-400">{stats.inactive_articles}</div>
              <div className="text-sm text-slate-500">Inactifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-800">{stats.total_categories}</div>
              <div className="text-sm text-slate-500">Catégories</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Categories Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderTree className="w-5 h-5" />
              Catégories
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                !selectedCategory ? 'bg-orange-100 text-orange-800' : 'hover:bg-slate-100'
              }`}
              onClick={() => setSelectedCategory(null)}
            >
              <Package className="w-4 h-4" />
              <span className="text-sm font-medium">Tous les articles</span>
            </div>
            <Separator className="my-2" />
            <div className="max-h-[400px] overflow-y-auto">
              {renderCategoryTree(categories)}
            </div>
          </CardContent>
        </Card>

        {/* Articles List */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg">
                Articles {selectedCategory && `- ${allCategories.find(c => c.id === selectedCategory)?.name || ''}`}
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    className="pl-9 w-48"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-inactive"
                    checked={showInactive}
                    onCheckedChange={(checked) => { setShowInactive(checked); setPage(0); }}
                  />
                  <Label htmlFor="show-inactive" className="text-sm text-slate-600">
                    Voir inactifs
                  </Label>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk Actions */}
            {selectedArticles.length > 0 && (
              <div className="mb-4 p-3 bg-slate-100 rounded-lg flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedArticles.length} article(s) sélectionné(s)
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setApplyMarginTo('selected'); setMarginDialogOpen(true); }}>
                    <Settings className="w-4 h-4 mr-1" /> Appliquer marge
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleBulkToggle(true)}>
                    <Eye className="w-4 h-4 mr-1" /> Activer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleBulkToggle(false)}>
                    <EyeOff className="w-4 h-4 mr-1" /> Désactiver
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedArticles([])}>
                    Annuler
                  </Button>
                </div>
              </div>
            )}
            
            {/* Global margin button */}
            {selectedArticles.length === 0 && (
              <div className="mb-4 flex justify-end gap-2">
                {selectedCategory && (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleToggleCategoryPrices(true)}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                    >
                      <Eye className="w-4 h-4 mr-1" /> 
                      Afficher tous les prix
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleToggleCategoryPrices(false)}
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    >
                      <EyeOff className="w-4 h-4 mr-1" /> 
                      Masquer tous les prix
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => { setApplyMarginTo(selectedCategory ? 'category' : 'all'); setMarginDialogOpen(true); }}>
                  <Settings className="w-4 h-4 mr-1" /> 
                  Appliquer marge {selectedCategory ? 'à cette catégorie' : 'à tous'}
                </Button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>Aucun article trouvé</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedArticles.length === articles.length && articles.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="w-16">Image</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead className="text-right">Prix Fournisseur</TableHead>
                      <TableHead className="text-right">Prix Vente</TableHead>
                      <TableHead className="text-center">Marge</TableHead>
                      <TableHead className="text-center">Prix Visible</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {articles.map((article) => (
                      <TableRow key={article.id} className={!article.is_active ? 'opacity-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedArticles.includes(article.id)}
                            onCheckedChange={(checked) => handleSelectArticle(article.id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          {article.image_url ? (
                            <img
                              src={article.image_url}
                              alt={article.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                              <ImageIcon className="w-5 h-5 text-slate-300" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-800 line-clamp-1">{article.name}</div>
                          {article.reference && (
                            <div className="text-xs text-slate-400">Réf: {article.reference}</div>
                          )}
                          {article.compatible_tonnage && (
                            <div className="text-xs text-slate-500">{article.compatible_tonnage}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {article.category_name || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-500">
                          {formatPrice(article.price, article.price_on_request)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {article.selling_price ? formatPrice(article.selling_price, false) : (
                            <span className="text-slate-400 text-xs">Non défini</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {article.margin_percent ? (
                            <Badge variant="secondary" className="text-xs">
                              {article.margin_percent}%
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={article.show_price !== false}
                            onCheckedChange={() => handleToggleShowPrice(article.id)}
                            className="data-[state=checked]:bg-green-500"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={article.is_active !== false}
                            onCheckedChange={() => handleToggleArticle(article.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditArticle(article)}>
                                <Edit className="w-4 h-4 mr-2" /> Modifier
                              </DropdownMenuItem>
                              {article.source_url && (
                                <DropdownMenuItem onClick={() => window.open(article.source_url, '_blank')}>
                                  <ExternalLink className="w-4 h-4 mr-2" /> Voir source
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDeleteArticle(article.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-slate-500">
                    {page * limit + 1} - {Math.min((page + 1) * limit, total)} sur {total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(page + 1) * limit >= total}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Article Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingArticle ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
            <DialogDescription>
              {editingArticle ? 'Modifiez les informations de l\'article' : 'Ajoutez un nouvel article au catalogue'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <Label>Nom *</Label>
              <Input
                value={articleForm.name}
                onChange={(e) => setArticleForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nom de l'article"
              />
            </div>
            <div className="space-y-2">
              <Label>Référence</Label>
              <Input
                value={articleForm.reference}
                onChange={(e) => setArticleForm(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="REF-001"
              />
            </div>
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select
                value={articleForm.category_id}
                onValueChange={(value) => setArticleForm(prev => ({ ...prev, category_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.parent_id ? `└ ${cat.name}` : cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prix Fournisseur HT (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={articleForm.price}
                onChange={(e) => handlePriceChange(e.target.value)}
                placeholder="0.00"
                disabled={articleForm.price_on_request}
              />
            </div>
            <div className="space-y-2 flex items-end">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="price-on-request"
                  checked={articleForm.price_on_request}
                  onCheckedChange={(checked) => setArticleForm(prev => ({ ...prev, price_on_request: checked }))}
                />
                <Label htmlFor="price-on-request">Prix sur demande</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Marge (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={articleForm.margin_percent}
                onChange={(e) => handleMarginChange(e.target.value)}
                placeholder="25"
                disabled={articleForm.price_on_request}
              />
            </div>
            <div className="space-y-2">
              <Label>Prix de Vente HT (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={articleForm.selling_price}
                onChange={(e) => handleSellingPriceChange(e.target.value)}
                placeholder="0.00"
                disabled={articleForm.price_on_request}
                className="border-green-300 focus:border-green-500"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Tonnage compatible</Label>
              <Input
                value={articleForm.compatible_tonnage}
                onChange={(e) => setArticleForm(prev => ({ ...prev, compatible_tonnage: e.target.value }))}
                placeholder="ex: 1.5 à 3.5 tonnes"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>URL Image</Label>
              <Input
                value={articleForm.image_url}
                onChange={(e) => setArticleForm(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Description courte</Label>
              <Input
                value={articleForm.short_description}
                onChange={(e) => setArticleForm(prev => ({ ...prev, short_description: e.target.value }))}
                placeholder="Description courte"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Description complète</Label>
              <Textarea
                value={articleForm.description}
                onChange={(e) => setArticleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description détaillée"
                rows={3}
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch
                id="is-active"
                checked={articleForm.is_active}
                onCheckedChange={(checked) => setArticleForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is-active">Article actif (visible)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveArticle} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingArticle ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nom de la catégorie"
              />
            </div>
            <div className="space-y-2">
              <Label>Slug *</Label>
              <Input
                value={categoryForm.slug}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                placeholder="nom-categorie"
              />
              <p className="text-xs text-slate-500">Identifiant unique (minuscules, tirets)</p>
            </div>
            <div className="space-y-2">
              <Label>Catégorie parente</Label>
              <Select
                value={categoryForm.parent_id || "none"}
                onValueChange={(value) => setCategoryForm(prev => ({ ...prev, parent_id: value === "none" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucune (catégorie principale)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune (catégorie principale)</SelectItem>
                  {allCategories.filter(c => !c.parent_id).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description de la catégorie"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Ordre d'affichage</Label>
              <Input
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveCategory} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCategory ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Margin Dialog */}
      <Dialog open={marginDialogOpen} onOpenChange={setMarginDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Appliquer une marge</DialogTitle>
            <DialogDescription>
              {applyMarginTo === 'selected' && `Appliquer à ${selectedArticles.length} article(s) sélectionné(s)`}
              {applyMarginTo === 'category' && `Appliquer à la catégorie "${allCategories.find(c => c.id === selectedCategory)?.name || ''}"`}
              {applyMarginTo === 'all' && 'Appliquer à tous les articles'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Marge (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={marginValue}
                onChange={(e) => setMarginValue(e.target.value)}
                placeholder="25"
              />
              <p className="text-xs text-slate-500">
                Exemple : 25% de marge sur un article à 1000€ fournisseur = 1250€ prix de vente
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarginDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleApplyMargin} className="bg-green-600 hover:bg-green-700">
              Appliquer la marge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Scrape TOOMAT Dialog */}
      <Dialog open={scrapeDialogOpen} onOpenChange={setScrapeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-600" />
              Import TOOMAT
            </DialogTitle>
            <DialogDescription>
              Importer les produits depuis le catalogue TOOMAT
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Last scrape info */}
            {scrapeStatus?.last_result && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Dernier import
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Catégories:</span>
                    <span className="font-medium">{scrapeStatus.last_result.categories_scraped || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Produits trouvés:</span>
                    <span className="font-medium">{scrapeStatus.last_result.products_found || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Créés:</span>
                    <span className="font-medium text-green-600">{scrapeStatus.last_result.products_created || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Mis à jour:</span>
                    <span className="font-medium text-blue-600">{scrapeStatus.last_result.products_updated || 0}</span>
                  </div>
                </div>
                {scrapeStatus.last_result.completed_at && (
                  <p className="text-xs text-slate-400 mt-2">
                    Terminé le {new Date(scrapeStatus.last_result.completed_at).toLocaleString('fr-FR')}
                  </p>
                )}
              </div>
            )}
            
            {/* Scraping status */}
            {scrapingInProgress && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Scraping en cours...</p>
                    <p className="text-sm text-blue-600">{scrapeStatus?.progress || 'Initialisation...'}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Options */}
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Le scraping va récupérer les produits du catalogue TOOMAT. 
                Les nouveaux produits seront créés, les existants mis à jour.
              </p>
              <div className="text-xs text-slate-500 space-y-1">
                <p>✓ Accessoires (godets, attaches, BRH...)</p>
                <p>✓ Pièces détachées (chenilles, filtres, galets...)</p>
                <p>✓ Prix public automatiquement importés</p>
              </div>
            </div>
            
            {/* Scheduled info */}
            <div className="p-3 bg-amber-50 rounded-lg text-sm">
              <p className="text-amber-800">
                <strong>Import automatique :</strong> Tous les jours à 6h00 (heure La Réunion)
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={handleSyncCategories}
              disabled={scrapingInProgress}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Synchroniser catégories
            </Button>
            <Button 
              onClick={() => handleStartScrape()}
              disabled={scrapingInProgress}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {scrapingInProgress ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  En cours...
                </>
              ) : (
                <>
                  <CloudDownload className="w-4 h-4 mr-2" />
                  Lancer l'import complet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessoiresFournisseurPage;
